import { useQuery } from '@urql/vue';
import { computed, ref, watch, watchEffect } from 'vue';

import { runAfterHydration } from '@/afterHydration';
import { useBreakpoints } from '@/composables/breakpoint';
import { useFischer } from '@/composables/fischerPlugin';
import GET_HANDLING_FEE_LIMIT from '@/graphql/getHandlingFeeLimit.gql';
import { useNavigationStore } from '@/store/pinia/navigation';

import { useHandlingFeeCookie } from './handlingFeeCookie';

/**
 * Derives the banner's store flags from the forwarded cookie, and nothing else - no query, no scroll
 * handler, and it never clears the cookie.
 *
 * Only the rendering that owns the banner needs these. The height offset every other rendering used
 * to take from a store flag now follows the banner's markup through a `:has()` rule, because the
 * server gives each rendering its own pinia (see renderSsrApp) while the client shares one across
 * the page - so a flag written by the header island was absent from another island's server render
 * and present in its hydration render, a class mismatch nothing downstream repairs.
 *
 * Exported for the page-gate tests; useHandlingFeeSetup is the only caller in the app.
 */
export const useHandlingFeeBannerFlags = () => {
  const navigationStore = useNavigationStore();
  const fischer = useFischer();
  const { getHandlingFeeLimitFormatted } = useHandlingFeeCookie();

  const handlingFeeLimit = ref<string | null>(null);
  const handlingFeeBannerScrollHandlerEnabled = ref<boolean>(false);

  const enableHandlingFeeBanner = computed<boolean>(
    () => navigationStore.enableHandlingFeeBanner === true,
  );

  /**
   * A watcher, not a one-shot call: the page type is not always known when this rendering sets up.
   * `store.context` is only reactive in the showroom, where ProductOrFamilyOrCategoryDevDummy writes
   * the flags after the header island's setup - a single look there finds nothing and no product page
   * ever gets the banner. In production the context is populated in setData before any island
   * renders, so nothing the effect reads changes and it runs exactly once, as the call did.
   *
   * The flag is only ever turned on, so a page type that arrives late can add the banner but a
   * re-run can never take it away mid-visit.
   *
   * `flush: 'sync'` is what keeps the server render honest. The gate is set up before the caller
   * derives the flags, and SSR gives a default watchEffect `once: true` - so it would look while
   * `enableHandlingFeeBanner` is still 'init', never look again, and leave the banner out of the
   * server markup for the client to add after mount.
   */
  watchEffect(
    () => {
      if (!enableHandlingFeeBanner.value || fischer.user.getSalesforce()) {
        return;
      }

      const currentUrl = fischer.uri();
      if (
        fischer.isCategoryPage() ||
        fischer.isFamilyPage() ||
        fischer.isSeriesPage() ||
        fischer.isVariantPage() ||
        fischer.pages.products().url === currentUrl ||
        fischer.pages.cart().url === currentUrl ||
        fischer.pages.checkout().url === currentUrl
      ) {
        navigationStore.setRequestHandlingFeeBanner(true);

        if (!IS_SSR) {
          handlingFeeBannerScrollHandlerEnabled.value = true;
        }
      }
    },
    { flush: 'sync' },
  );

  const setHandlingFeeLimit = (value: string | null) => {
    handlingFeeLimit.value = value;
    navigationStore.setEnableHandlingFeeBanner(value !== null);
  };

  /**
   * The cookie is keyed by fischer.user.customerId(), which in an assisted service session is the
   * impersonated customer - known only in the frontend, so the server always reads the cookie as
   * somebody else's and renders no banner. Writing the flags after hydration instead of during
   * setup lets the first client render agree with that markup.
   */
  const isImpersonating = () => !IS_SSR && Boolean(fischer.user.getAssistedService()?.user);

  /** Only reads. Clearing a stale cookie stays with useHandlingFeeSetup, which owns the banner. */
  const deriveHandlingFeeBanner = () => {
    if (fischer.shop.isClosedShop() && fischer.user.accessToken()) {
      if (isImpersonating()) {
        runAfterHydration(() => setHandlingFeeLimit(getHandlingFeeLimitFormatted()));
      } else {
        setHandlingFeeLimit(getHandlingFeeLimitFormatted());
      }
      return true;
    }
    return false;
  };

  return {
    handlingFeeLimit,
    handlingFeeBannerScrollHandlerEnabled,
    setHandlingFeeLimit,
    deriveHandlingFeeBanner,
  };
};

export const useHandlingFeeSetup = () => {
  const navigationStore = useNavigationStore();
  const { windowScrollPos } = useBreakpoints();
  const fischer = useFischer();

  const {
    clearHandlingFeeLimitCookie,
    getHandlingFeeLimitCookie,
    setHandlingFeeLimitCookie,
    getHandlingFeeLimitFormatted,
  } = useHandlingFeeCookie();

  const {
    handlingFeeLimit,
    handlingFeeBannerScrollHandlerEnabled,
    setHandlingFeeLimit,
    deriveHandlingFeeBanner,
  } = useHandlingFeeBannerFlags();

  const requestHandlingFeeBanner = computed<boolean>(
    () => navigationStore.requestHandlingFeeBanner,
  );
  const isScrolledOutHandlingFeeBanner = computed<boolean>(
    () => navigationStore.isScrolledOutHandlingFeeBanner,
  );

  watchEffect(() => {
    if (IS_SSR || !handlingFeeBannerScrollHandlerEnabled.value) {
      return;
    }

    const scrollPosition = windowScrollPos.value;
    const hideThreshold = isScrolledOutHandlingFeeBanner.value ? 250 : 300; // make sure we do not cycle between 'scrolled in' and 'scrolled out' because we are at the edge
    const hideBanner = scrollPosition.y > hideThreshold;
    if (hideBanner !== isScrolledOutHandlingFeeBanner.value) {
      navigationStore.setIsScrolledOutHandlingFeeBanner(hideBanner);
    }
  });

  if (!deriveHandlingFeeBanner()) {
    clearHandlingFeeLimitCookie();
  }

  const enabled = computed<boolean>(() =>
    Boolean(
      !IS_SSR &&
      !(
        IS_SHOWROOM &&
        fischer.user.getAssistedService() &&
        !fischer.user.getAssistedService()?.user
      ) &&
      !getHandlingFeeLimitCookie()?.noBanner &&
      handlingFeeLimit.value === null &&
      fischer.shop.isClosedShop() &&
      fischer.user.accessToken() &&
      (!fischer.user.getAssistedService() || fischer.user.getAssistedService()?.user),
    ),
  );
  const { data, error } = useQuery({
    query: GET_HANDLING_FEE_LIMIT,
    variables: {
      baseSiteId: fischer.baseSiteId(),
      userId: fischer.user.id(),
      accessToken: fischer.user.accessToken(),
      division: fischer.user.division(),
      distributionChannel: fischer.user.distributionChannel(),
    },
    pause: computed(() => !enabled.value),
  });

  watch(error, (e) => {
    if (e) console.error('could not get handling fee limit', e);
  });
  const queryResult = computed(() => data.value?.getHandlingFeeLimit);

  watch(
    queryResult,
    (value: { handlingFeeLimit: number; currency: { isocode: string } } | null) => {
      const handlingFeeLimitResult = value?.handlingFeeLimit;
      if (handlingFeeLimitResult) {
        setHandlingFeeLimitCookie(handlingFeeLimitResult, value.currency?.isocode, false);
        setHandlingFeeLimit(getHandlingFeeLimitFormatted());
      }
    },
  );

  return {
    handlingFeeLimit,
    requestHandlingFeeBanner,
  };
};
